import { useMemo, useState } from 'react';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { Icon, Typography, TextArea, Button } from '@scale/scaleui';
import { ProcessSupervisionChunk } from './types';
import { Alert } from '../../TempComponents';
import TwoTextSxSDiffView from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/TwoTextSxSDiffView';
import { validateStepContentMatches } from './helpers';

const useLocalProcessSteps = (initialSteps: ProcessSupervisionChunk[]) => {
  const [localProcessSteps, setLocalProcessSteps] = useState(
    initialSteps.length ? [...initialSteps] : [{ content: '' }],
  );
  return [localProcessSteps, setLocalProcessSteps] as const;
};

const handleDragEnd = (
  result: DropResult,
  setLocalProcessSteps: (
    steps:
      | ProcessSupervisionChunk[]
      | ((prevSteps: ProcessSupervisionChunk[]) => ProcessSupervisionChunk[]),
  ) => void,
) => {
  if (!result.destination) {
    return;
  }
  const startIndex = result.source.index;
  const endIndex = result.destination.index;
  setLocalProcessSteps(localProcessSteps => {
    const newProcessSteps = [...localProcessSteps];
    const [removed] = newProcessSteps.splice(startIndex, 1);
    newProcessSteps.splice(endIndex, 0, removed);
    return newProcessSteps;
  });
};

export const ProcessStepsEditor = ({
  title,
  processSteps,
  setProcessSteps,
  originalContent,
  onFinish,
  disableManualChunkValidation = false,
}: {
  title: string;
  processSteps: ProcessSupervisionChunk[];
  setProcessSteps: (processSteps: ProcessSupervisionChunk[]) => void;
  originalContent: string | undefined;
  onFinish: () => void;
  disableManualChunkValidation?: boolean;
}) => {
  const [localProcessSteps, setLocalProcessSteps] = useLocalProcessSteps(processSteps);
  const numSteps = localProcessSteps.length;

  const handleFinish = () => {
    setProcessSteps(localProcessSteps);
    onFinish();
  };

  const validation = useMemo(() => {
    // If originalContent is undefined, no validation is required, and the process is considered successful by default.
    if (!originalContent) {
      return {
        success: true,
      } as const;
    }
    return validateStepContentMatches(localProcessSteps, originalContent);
  }, [localProcessSteps, originalContent]);

  return (
    <div className="flex flex-col gap-2">
      <Typography.Subtitle1>{title}</Typography.Subtitle1>
      {originalContent && (
        <TwoTextSxSDiffView
          textOne={originalContent}
          textTwo={localProcessSteps.map(step => step.content).join('')}
          textOneTitle="Original"
          textTwoTitle="Chunked"
          variant="stretch"
        />
      )}
      <div className="flex flex-col gap-2">
        <Typography.Subtitle1>Chunks</Typography.Subtitle1>
        <DragDropContext
          onDragEnd={(result: DropResult) => handleDragEnd(result, setLocalProcessSteps)}
        >
          <Droppable droppableId="droppable">
            {(provided, _snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="flex flex-col gap-2"
              >
                {Array.from({ length: numSteps }, (_, index) => {
                  return (
                    <Draggable key={`${index}}`} draggableId={`draggable-${index}`} index={index}>
                      {(provided, _snapshot) => (
                        <div
                          className="flex items-center gap-2"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <div {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                            <Icon variant="neutral" size="md" icon="grip-vertical" />
                          </div>
                          <TextArea
                            key={index}
                            id={`processStep-${index}`}
                            value={localProcessSteps?.[index]?.content || ''}
                            onChange={e => {
                              setLocalProcessSteps(localProcessSteps => {
                                const newLocalProcessSteps = [...localProcessSteps];
                                newLocalProcessSteps[index] = {
                                  ...newLocalProcessSteps[index],
                                  content: e.target.value,
                                  isEdited: true,
                                };
                                return newLocalProcessSteps;
                              });
                            }}
                            dynamic
                            className="flex flex-1 w-full"
                            textAreaWrapperClassName="flex flex-1"
                            placeholder={`(e.g.) Step ${index + 1}: ...`}
                          />
                          <Button
                            onClick={() => {
                              setLocalProcessSteps(localProcessSteps => {
                                const newLocalProcessSteps = [...localProcessSteps];
                                newLocalProcessSteps.splice(index, 1);
                                return newLocalProcessSteps;
                              });
                            }}
                            variant="neutral"
                            className="p-1"
                          >
                            <Icon icon="trash" variant="neutral" size="sm" />
                          </Button>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      {/* add another step */}
      <Button
        onClick={() => {
          setLocalProcessSteps(localProcessSteps => [...localProcessSteps, { content: '' }]);
        }}
        variant="primary"
        className="max-w-[160px]"
      >
        {/* @ts-ignore */}
        <Icon icon="plus" className="mr-2" variant="white" />
        Add another step
      </Button>
      {validation?.success === false && validation?.error && (
        <Alert variant="warning" className="my-2">
          <Typography.Paragraph2>{validation?.error?.message}</Typography.Paragraph2>
        </Alert>
      )}

      <div className="flex flex-row-reverse gap-2 w-full mb-2">
        <Button
          variant="primary"
          onClick={handleFinish}
          disabled={!disableManualChunkValidation && validation.success === false}
        >
          {/* @ts-ignore */}
          <Icon variant="white" icon="check" size="sm" className="mr-2" />
          Save chunks
        </Button>
      </div>
    </div>
  );
};
